import { Link } from "react-router-dom";
import { useI18n } from "@/i18n/I18nProvider";
import { SEO } from "@/components/SEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { InternalLinks } from "@/components/InternalLinks";
import { providers } from "@/data/providers";
import { providerPath } from "@/lib/format";

export function ProvidersHubPage() {
  const { t, lang, prefix } = useI18n();
  const isSv = lang === "sv";
  const title = isSv ? "Speltillverkare 2026 — studiorna bakom spelen" : "Software providers 2026 — the studios behind the games";
  const desc = isSv
    ? "De största spelleverantörerna hos svenska casinon. NetEnt, Play'n GO, Pragmatic Play, Evolution, Hacksaw Gaming, Microgaming och fler."
    : "The biggest software providers at Swedish casinos: NetEnt, Play'n GO, Pragmatic Play, Evolution, Hacksaw Gaming, Microgaming and more.";

  return (
    <div className="container-page py-8">
      <SEO title={title} description={desc} />
      <Breadcrumbs items={[{ label: t("nav.home"), href: prefix }, { label: title }]} />
      <h1 className="mt-3 text-3xl font-extrabold md:text-4xl">{title}</h1>
      <p className="mt-2 max-w-3xl text-ink-700">{desc}</p>

      <article className="prose prose-ink mt-6 max-w-3xl [&>h2]:mt-8 [&>h2]:mb-3 [&>h2]:text-2xl [&>h2]:font-bold [&>p]:my-3">
        <h2>{isSv ? "Vem gör spelen?" : "Who makes the games?"}</h2>
        <p>
          {isSv
            ? "Spelen du spelar på casinon utvecklas inte av casinot självt utan av separata speltillverkare. De största i Sverige är NetEnt och Play'n GO, båda svenska studior som format dagens slot-upplevelse, samt internationella jättar som Evolution, Pragmatic Play, Microgaming och Hacksaw Gaming. Varje studio har sin egen signatur — vissa fokuserar på högvolatila slots, andra på livecasino, jackpotts eller bonusspelmekanik."
            : "The games you play at casinos are not made by the casino itself but by separate game studios. The biggest in Sweden are NetEnt and Play'n GO, both Swedish studios that shaped today's slot experience, alongside international giants such as Evolution, Pragmatic Play, Microgaming and Hacksaw Gaming. Each studio has a signature — some focus on high-volatility slots, others on live casino, jackpots or bonus mechanics."}
        </p>
        <h2>{isSv ? "Varför studio spelar roll" : "Why the studio matters"}</h2>
        <p>
          {isSv
            ? "Studion bestämmer matematiken, designen och bonusfunktionerna. Två slots från samma operatör men olika studios kan ha helt olika känsla. Om du tycker om en specifik slots stil — exempelvis Hacksaw Gamings höga volatilitet — är chansen stor att du gillar andra titlar från samma studio."
            : "The studio decides the maths, design and bonus mechanics. Two slots at the same operator but from different studios can feel completely different. If you like a specific slot's style — for example Hacksaw Gaming's high volatility — chances are you will enjoy other titles from the same studio."}
        </p>
      </article>

      <h2 className="mt-10 text-2xl font-bold">
        {isSv ? "Alla speltillverkare vi täcker" : "All providers we cover"}
      </h2>
      <div className="mt-4 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {providers.map((p) => (
          <Link
            key={p.id}
            to={providerPath(prefix, lang, p.slug)}
            className="card-interactive p-5 no-underline"
          >
            <h3 className="text-lg font-semibold">{p.name}</h3>
            <p className="mt-2 text-sm text-ink-700 line-clamp-4">
              {isSv ? p.description_sv : p.description_en}
            </p>
          </Link>
        ))}
      </div>

      <InternalLinks context="provider" />
    </div>
  );
}
